import {
  Grid,
  Typography,
  styled,
  Link,
  GridProps,
  Box,
  Button,
  Stack,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { BoxLogo } from "./BoxLogo";
import { BoxBody } from "./BoxBody";
import StudentCard from "./StudentCard";
import CertificateCard from "./CertificateCard";
import {
  FloatingWhatsApp,
  MoreCoursesOrWorkshops,
  SearchForm,
} from "./SearchForm";
import { useStudentStore } from "../../../hooks/useStudentStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiStatus } from "../../../types/api/status";
import { CustomBackdrop } from "../../../components/common/CustomBackdrop/CustomBackdrop";
import { SocialSection } from "./SocialSection";
import { Footer } from "./Footer";
import WorkshopCard from "./WorkshopCard";
import RewardsDetail from "./RewardsDetail";
import { RewardStudent } from "../../../types/slices/rewardStudentType";
import { Modal } from "../../../components/common/Modal/Modal";
import { ModalBody } from "../../../components/common/Modal/ModalBody";
import { ModalHeader } from "../../../components/common/Modal/ModalHeader";
import { CustomSnackbar } from "../../../components/common/CustomSnackbar/CustomSnackbar";
import { ValidateSignatureModal } from "./ValidateSignatureModal";

const WATANA_ENTITY_CODES = [
  "watana-focus",
  "watana-corlad",
  "watana-corlad-provincias",
];

const isWatanaCertificate = (data: { entity_code?: string }): boolean =>
  Boolean(data?.entity_code && WATANA_ENTITY_CODES.includes(data.entity_code));

const GridBody = styled(Grid)<GridProps>(({ theme }) => ({
  backgroundColor: "#F7F9FC",
  minWidth: 0,
  [theme.breakpoints.up("md")]: { height: "100vh", overflow: "hidden scroll" },
}));

const GridContainer = styled(Grid)<GridProps>(({ theme }) => ({
  minHeight: "100vh",
  [theme.breakpoints.up("md")]: {
    height: "100vh",
  },
}));


const base64ToBlob = (
  base64Data: string,
  contentType = "application/pdf"
): Blob => {
  const byteCharacters = atob(base64Data);
  const sliceSize = 1024;
  const byteArrays: BlobPart[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: contentType });
};

const isIOSMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const triggerPdfDownload = async (
  base64File: string,
  filename = "documento.pdf"
): Promise<void> => {
  if (!base64File) return;

  const blob = base64ToBlob(base64File, "application/pdf");

  if (isIOSMobileDevice() && typeof navigator !== "undefined" && "share" in navigator) {
    const file = new File([blob], filename, { type: "application/pdf" });
  
    try {
      
      const canShare = typeof navigator.canShare === "function"
        ? 
          navigator.canShare({ files: [file] })
        : true;
  
      if (canShare) {
        
        await navigator.share({
          files: [file],
          title: filename,
          text: filename,
        });
        return; 
      }
    } catch (e) {
      
    }
  }
  

  const blobUrl = URL.createObjectURL(blob);

  if (isIOSMobileDevice()) {
    window.location.assign(blobUrl);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return;
  }

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
};

const Certificates = ({ dataCertificate }: any) => {
  const { getCertificatePDF } = useStudentStore();
  const [isShowExpiredMessage, setIsShowExpiredMessage] = useState(false);
  const [openValidateModal, setOpenValidateModal] = useState(false);

  const downloadCertificate = useCallback(
    async (data: any) => {
      const { codigo, is_expired } = data;

      if (is_expired) {
        setIsShowExpiredMessage(true);
        return;
      }

      if (isWatanaCertificate(data)) {
        const signedUrl = typeof data.url === "string" ? data.url.trim() : "";
        if (signedUrl) {
          window.open(signedUrl, "_blank", "noopener,noreferrer");
          return;
        }
        CustomSnackbar(
          "warning",
          "Tu certificado firmado aún no está disponible. Vuelve a intentar más tarde."
        );
        return;
      }

      try {
        const response = await getCertificatePDF(codigo);
        if (!response?.file) return;

        await triggerPdfDownload(
          response.file,
          response.filename || "certificado.pdf"
        );
      } catch (e) {
        console.warn("No se pudo descargar. Volver a intentar.");
      }
    },
    [setIsShowExpiredMessage, getCertificatePDF]
  );

  const onOpenValidateModal = useCallback((data: any) => {
    if (!isWatanaCertificate(data)) {
      return;
    }
    setOpenValidateModal(true);
  }, []);

  return (
    <>
      {!dataCertificate.length && (
        <MoreCoursesOrWorkshops message="No cuentas con certificados, descubre nuestros nuevos cursos." />
      )}
      {Boolean(dataCertificate.length) && (
        <>
          <Box sx={{ display: { xs: "none", sm: "block" }, width: "100%", maxWidth: 860, mx: "auto", mb: 2.5 }}>
            <Typography
              variant="h5"
              sx={{ color: "primary.dark", fontSize: { xs: 20, sm: 24 }, fontWeight: 700 }}
            >
              Mis certificados
            </Typography>
          </Box>

          {dataCertificate.map((data: any) => {
            return (
              <CertificateCard
                key={data.codigo}
                data={data}
                onClick={downloadCertificate}
                isWatana={isWatanaCertificate(data)}
                onValidateSignature={onOpenValidateModal}
                baseColor="#014DDF"
              />
            );
          })}

          <ValidateSignatureModal
            open={openValidateModal}
            onClose={() => setOpenValidateModal(false)}
          />

          <Modal
            open={isShowExpiredMessage}
            onClose={() => setIsShowExpiredMessage(false)}
            size="sm"
          >
            <ModalHeader
              text={
                <Box textAlign="center" width="100%">
                  <div>
                    <img alt="constancia" src="./icons/peligro.svg" width={60} />
                  </div>
                  <Typography
                    className="ml2"
                    sx={{
                      textAlign: "center",
                      fontSize: 24,
                      fontWeight: 600,
                      color: "#0c46ff",
                    }}
                  >
                    Certificado no disponible
                  </Typography>
                </Box>
              }
              className="positionElements"
              onCancel={() => setIsShowExpiredMessage(false)}
            />
            <ModalBody dividers={false} padding={4}>
              <Box sx={{ px: 2, pb: 3, textAlign: "center" }}>
                <Typography sx={{ color: "text.primary", fontSize: 14, lineHeight: 1.65 }}>
                  La descarga venció porque pasaron <strong>más de dos años</strong> desde su emisión.
                </Typography>
                <Typography sx={{ mt: 2, color: "text.secondary", fontSize: 13.5, lineHeight: 1.6 }}>
                  Tu registro y formación siguen guardados. Si necesitas ayuda, contáctanos por nuestros canales de atención.
                </Typography>
              </Box>
            </ModalBody>
          </Modal>
        </>
      )}
    </>
  );
};

const Workshops = ({ dataWorkshop }: any) => {
  const { getWorkshopPDF } = useStudentStore();

  const downloadWorkshop = async (codigo: string) => {
    try {
      const response = await getWorkshopPDF(codigo);
      if (!response?.file) return;

      await triggerPdfDownload(
        response.file,
        response.filename || "constancia.pdf"
      );
    } catch (e) {
      console.warn("No se pudo descargar. Volver a intentar.");
    }
  };
  return (
    <>
      {!dataWorkshop.length && (
        <MoreCoursesOrWorkshops message="No cuentas con constancias, descubre nuestros nuevos talleres." />
      )}
      {Boolean(dataWorkshop.length) && (
        <>
          <Box sx={{ display: { xs: "none", sm: "block" }, width: "100%", maxWidth: 860, mx: "auto", mb: 2.5 }}>
            <Typography
              variant="h5"
              sx={{ color: "primary.dark", fontSize: { xs: 20, sm: 24 }, fontWeight: 700 }}
            >
              Mis constancias
            </Typography>
          </Box>

          {dataWorkshop.map((data: any) => {
            return (
              <WorkshopCard
                key={data.codigo}
                data={data}
                onClick={downloadWorkshop}
                baseColor="#6C5CE7"
              />
            );
          })}
        </>
      )}
    </>
  );
};

const ResultTab = ({ dataCertificate, dataWorkshop }: any) => {
  const [value, setValue] = useState("1");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <>
      <Box sx={{ width: "100%", maxWidth: 980, typography: "body1" }}>
        <Box
          className="focus-result-hero"
          component="section"
          sx={{
            position: "relative",
            overflow: "hidden",
            mb: { xs: 1.5, sm: 2.5 },
            px: { xs: 2.5, sm: 4 },
            py: { xs: 2, sm: 3.5 },
            color: "common.white",
            borderRadius: { xs: 3, sm: 4 },
            background:
              "linear-gradient(125deg, #0A229C 0%, #014DDF 60%, #0008DE 100%)",
            boxShadow: "0 18px 42px rgba(10, 34, 156, 0.2)",
            "&::after": {
              content: "\"\"",
              position: "absolute",
              width: 220,
              height: 220,
              top: -125,
              right: -45,
              borderRadius: "50%",
              border: "42px solid rgba(0, 217, 255, 0.18)",
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ display: { xs: "none", sm: "flex" }, position: "relative", zIndex: 1 }}
          >
            <WorkspacePremiumRoundedIcon sx={{ color: "#00D9FF", fontSize: 20 }} />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
              }}
            >
              Mi formación Focus
            </Typography>
          </Stack>
          <Typography
            variant="h4"
            sx={{
              position: "relative",
              zIndex: 1,
              mt: { xs: 0, sm: 1 },
              fontSize: { xs: 22, sm: 34 },
              fontWeight: 700,
            }}
          >
            {value === "1" ? "Mis certificados Focus" : "Mis constancias Focus"}
          </Typography>
          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              mt: 0.75,
              display: { xs: "none", sm: "block" },
              color: "rgba(255,255,255,0.76)",
              fontSize: 14,
            }}
          >
            Todo lo que lograste, listo para descargar.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ position: "relative", zIndex: 1, mt: { xs: 1, sm: 2.25 } }}>
            <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: 0.65, borderRadius: 2, bgcolor: "rgba(255,255,255,0.14)", fontSize: { xs: 11, sm: 12 }, fontWeight: 600 }}>
              {dataCertificate.length} certificado{dataCertificate.length === 1 ? "" : "s"}
            </Box>
            <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: 0.65, borderRadius: 2, bgcolor: "rgba(255,255,255,0.14)", fontSize: { xs: 11, sm: 12 }, fontWeight: 600 }}>
              {dataWorkshop.length} constancia{dataWorkshop.length === 1 ? "" : "s"}
            </Box>
          </Stack>
        </Box>
        <TabContext value={value}>
          <Box
            className="focus-result-tabs"
            sx={{
              p: { xs: 0.5, sm: 0.75 },
              textAlign: "center",
              maxWidth: 980,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              bgcolor: "background.paper",
            }}
          >
            <TabList
              onChange={handleChange}
              aria-label="Mis documentos Focus"
              centered
              variant="fullWidth"
              sx={{
                minHeight: 44,
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: { xs: 44, sm: 46 },
                  borderRadius: 2,
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "none",
                  transition: "color 180ms ease, background-color 180ms ease, box-shadow 180ms ease",
                },
                "& .Mui-selected": {
                  color: "primary.dark",
                  bgcolor: "primary.light",
                  boxShadow: "inset 0 0 0 1px rgba(1,77,223,0.08)",
                },
              }}
            >
              <Tab label={`Cursos (${dataCertificate.length})`} value="1" />
              <Tab label={`Talleres (${dataWorkshop.length})`} value="2" />
            </TabList>
          </Box>
          <TabPanel className="focus-tab-panel" value="1" sx={{ px: { xs: 0, sm: 2 }, pt: { xs: 2, sm: 3 } }}>
            <Certificates dataCertificate={dataCertificate} />
          </TabPanel>
          <TabPanel className="focus-tab-panel" value="2" sx={{ px: { xs: 0, sm: 2 }, pt: { xs: 2, sm: 3 } }}>
            <Workshops dataWorkshop={dataWorkshop} />
          </TabPanel>
        </TabContext>
      </Box>
    </>
  );
};

export const ResultLayout = () => {
  const {
    status: statusCertificate,
    getCertificateByDNI,
    getWorkshopByDNI,
    getRewardStudent,
  } = useStudentStore();

  const [dataCertificate, setDataCertificate] = useState<any[]>([]);
  const [dataWorkshop, setDataWorkshop] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [, setError] = useState<boolean>(false);
  const [showRewardsDetail, setShowRewardsDetail] = useState<boolean>(false);
  const [wonPoints, setWonPoints] = useState<RewardStudent[]>([]);
  const [exchangedPoints, setExchangedPoints] = useState<RewardStudent[]>([]);

  const handleSearchCertificate = useCallback(
    async (dni: string): Promise<void> => {
      setLoading(true);
      if (!dni.trim()) {
        setLoading(false);
        return;
      }
      const responseCertificate = await getCertificateByDNI(dni);
      const responseWorkshop = await getWorkshopByDNI(dni);

      if (responseCertificate.status || responseCertificate.length > 0) {
        setDataCertificate(responseCertificate);
      } else {
        setError(true);
      }
      if (responseWorkshop.status || responseWorkshop.length > 0) {
        setDataWorkshop(responseWorkshop);
      } else {
        setError(true);
      }
      if (!responseCertificate?.length && !responseWorkshop?.length) {
        CustomSnackbar(
          "warning",
          "No encontramos documentos con ese DNI o carné de extranjería."
        );
      }
      setLoading(false);
    },
    [
      getCertificateByDNI,
      getWorkshopByDNI,
      setLoading,
      setDataWorkshop,
      setDataCertificate,
      setError,
    ]
  );

  const onAnotherDNIClick = useCallback(() => {
    setDataCertificate([]);
    setDataWorkshop([]);
    setShowRewardsDetail(false);
  }, [setDataCertificate, setDataWorkshop]);

  const [totalPoints, setTotalPoints] = useState(0);

  const dataStudent = useMemo(
    () => dataCertificate[0] || dataWorkshop[0],
    [dataCertificate, dataWorkshop]
  );

  useEffect(() => {
    if (dataStudent?.dni_student) {
      (async () =>
        await getRewardStudent(dataStudent.dni_student).then((data) => {
          if (data && data.detail) {
            const total = data.detail.reduce(
              (acc: number, { points }: any) => acc + points,
              0
            );
            setTotalPoints(total);

            const exchangeData = data.detail.filter(({ type }: any) => type === "C");
            const wonData = data.detail.filter(({ type }: any) => type === "G");

            const mappedWonData = wonData.map((d: any) => ({
              ...d,
              issueDate: new Date(d.issue_date.replace(/-/gi, "/")),
            }));
            const mappedExchangeData = exchangeData.map((d: any) => ({
              ...d,
              issueDate: new Date(d.issue_date.replace(/-/gi, "/")),
            }));

            setWonPoints(mappedWonData);
            setExchangedPoints(mappedExchangeData);
          }
        }))();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataStudent]);

  const hasResults = dataCertificate.length > 0 || dataWorkshop.length > 0;

  return (
    <div style={{ flexGrow: 1 }}>
      {statusCertificate === ApiStatus.FETCHING && <CustomBackdrop open={true} />}
      <GridContainer container>
        <Grid
          item
          md={5}
          lg={4}
          xs={12}
          className={`blue-block${hasResults ? " has-results" : ""}`}
          sx={{
            position: "relative",
            overflow: "hidden",
            isolation: "isolate",
            background:
              "linear-gradient(155deg, #0A229C 0%, #014DDF 56%, #0008DE 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            minHeight: { xs: "auto", md: "100vh" },
            py: { xs: hasResults ? 0.5 : 1.5, md: 0 },
            "&::before": {
              content: '""',
              position: "absolute",
              zIndex: -1,
              width: { xs: 240, md: 430 },
              height: { xs: 240, md: 430 },
              top: { xs: -170, md: -210 },
              right: { xs: -85, md: -170 },
              borderRadius: "50%",
              border: {
                xs: "42px solid rgba(0,217,255,0.12)",
                md: "72px solid rgba(0,217,255,0.13)",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              zIndex: -1,
              width: { xs: 200, md: 360 },
              height: { xs: 200, md: 360 },
              left: { xs: -140, md: -230 },
              bottom: { xs: -130, md: 70 },
              borderRadius: "50%",
              background: "rgba(108,92,231,0.32)",
            },
          }}
        >
          <BoxLogo className={hasResults ? "has-results" : ""}>
            <Link
              href=""
              target="_self"
              aria-label="Volver al inicio de Focus"
              sx={{ minHeight: 44, display: "inline-flex", alignItems: "center", cursor: "pointer" }}
            >
              <img
                className="logo"
                alt="Focus Centro de Formación"
                src="/NUEVO_CFE_LOGO.png"
                width="100%"
              />
            </Link>

            {!loading && (dataCertificate.length > 0 || dataWorkshop.length > 0) && (
              <>
                <StudentCard data={dataStudent} />
                <Box
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    textAlign: "left",
                    borderRadius: { xs: 2, md: 3 },
                    p: { xs: 1.15, md: 2.25 },
                    color: "common.white",
                    background:
                      "linear-gradient(135deg, #6C5CE7 0%, #4100DE 100%)",
                    border: "1px solid rgba(255,255,255,0.22)",
                    width: "100%",
                    mt: { xs: 0.75, md: 1.25 },
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, md: 1.5 },
                    boxShadow: "0 16px 34px rgba(30,0,112,0.28)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: 110,
                      height: 110,
                      right: -55,
                      top: -65,
                      borderRadius: "50%",
                      border: "22px solid rgba(0,217,255,0.15)",
                    },
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: { xs: 38, md: 48 },
                      height: { xs: 38, md: 48 },
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      color: "#FDCB6E",
                      bgcolor: "rgba(255,255,255,0.14)",
                      borderRadius: 2,
                    }}
                  >
                    <EmojiEventsRoundedIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      position: "relative",
                      zIndex: 1,
                      display: { xs: "flex", md: "block" },
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 10, md: 12 },
                        opacity: 0.78,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Mis puntos
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 16, md: 23 }, fontWeight: 700, lineHeight: 1.2 }}>
                      {totalPoints} puntos
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => setShowRewardsDetail(true)}
                    aria-label="Ver mis movimientos de puntos"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      minWidth: 44,
                      minHeight: 44,
                      px: { xs: 1, md: 1.25 },
                      color: "common.white",
                      bgcolor: "rgba(255,255,255,0.12)",
                      fontSize: { xs: 0, md: 12 },
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      "& .MuiButton-endIcon": { m: 0 },
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    Movimientos
                  </Button>
                </Box>
              </>
            )}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <SocialSection />
            </Box>
          </BoxLogo>
        </Grid>
        <GridBody item md={7} lg={8} xs={12}>
          {(dataCertificate.length > 0 || dataWorkshop.length > 0) && (
            <Box
              sx={{
                width: "100%",
                px: { xs: 2, md: 3 },
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                top: 0,
                position: "sticky",
                zIndex: 10,
              }}
            >
              <Button
                type="button"
                onClick={onAnotherDNIClick}
                color="primary"
                variant="text"
                startIcon={<ArrowBackRoundedIcon />}
                sx={{ minHeight: 44, fontWeight: 600 }}
              >
                Nueva consulta
              </Button>
              <Typography
                variant="caption"
                sx={{
                  display: { xs: "none", sm: "block" },
                  color: "text.secondary",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Validación de documentos
              </Typography>
            </Box>
          )}
          <BoxBody>
            {showRewardsDetail && (
              <RewardsDetail
                onclick={() => setShowRewardsDetail(false)}
                points={totalPoints}
                wonPoints={wonPoints}
                exchangedPoints={exchangedPoints}
              />
            )}
            {!showRewardsDetail &&
              dataCertificate.length === 0 &&
              dataWorkshop.length === 0 && (
                <Box
                  component="main"
                  sx={{
                    position: { xs: "relative", md: "absolute" },
                    top: { md: "50%" },
                    left: { md: "50%" },
                    transform: { md: "translate(-50%, -50%)" },
                    width: { xs: "100%", md: "calc(100% - 64px)" },
                    maxWidth: 720,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2.5,
                    mx: { xs: "auto", md: 0 },
                    my: { xs: "auto", md: 0 },
                  }}
                >
                  <SearchForm onClick={handleSearchCertificate} />
                </Box>
              )}
            {!showRewardsDetail &&
              !loading &&
              dataCertificate &&
              dataWorkshop &&
              (dataCertificate.length > 0 || dataWorkshop.length > 0) && (
                <ResultTab
                  dataCertificate={dataCertificate}
                  dataWorkshop={dataWorkshop}
                />
              )}
            <Footer />
            <Box sx={{ display: { xs: "block", md: "none" }, width: "100%", mt: 1.5 }}>
              <SocialSection />
            </Box>
          </BoxBody>
        </GridBody>
      </GridContainer>
      <FloatingWhatsApp />
    </div>
  );
};
